import { useState } from 'react';
import type { OrderStep } from '../../shared/orderStatus';
import { formatDate, relativeTime } from '../utils/time';
import { TrackingDetails } from './TrackingDetails';

interface StepIndicatorProps {
  step: OrderStep;
  isLast: boolean;
  isCore?: boolean;
}

const nodeClasses = {
  completed: 'bg-step-completed border-step-completed text-white',
  active: 'bg-step-active border-step-active text-brand-900',
  pending: 'bg-white border-step-pending text-step-pending',
};

const lineClasses = {
  completed: 'bg-step-completed',
  active: 'bg-gradient-to-b from-step-active to-step-pending',
  pending: 'bg-slate-200',
};

export function StepIndicator({ step, isLast, isCore }: StepIndicatorProps) {
  const hasDetails =
    !!step.details &&
    !!(
      (step.details.trackingNo && step.details.carrier) ||
      step.details.expirationDate ||
      step.details.assemblyStage ||
      step.details.inspectionResult ||
      step.details.coreBalance !== undefined
    );
  const [showDetails, setShowDetails] = useState(hasDetails);

  const iconForStatus = () => {
    if (step.status === 'completed') {
      return (
        <svg
          className="w-3.5 h-3.5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={3}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      );
    }
    if (step.status === 'active') {
      return (
        <div className="relative">
          <div className="w-2 h-2 rounded-full bg-brand-900" />
          <div className="absolute inset-0 w-2 h-2 rounded-full bg-brand-900 animate-pulse-ring" />
        </div>
      );
    }
    return <div className="w-2 h-2 rounded-full bg-step-pending" />;
  };

  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <div
          className={`
            relative z-10 flex items-center justify-center
            w-7 h-7 rounded-full border-2 transition-all duration-300 shrink-0
            ${nodeClasses[step.status]}
            ${step.status === 'active' ? 'shadow-lg shadow-step-active/30 scale-110' : ''}
          `}
        >
          {iconForStatus()}
        </div>
        {!isLast && (
          <div
            className={`w-0.5 flex-1 min-h-8 ${lineClasses[step.status]} transition-colors duration-500`}
          />
        )}
      </div>

      <div className={`pb-6 ${isLast ? 'pb-0' : ''} min-w-0 flex-1`}>
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={`
              text-sm leading-7
              ${step.status === 'active' ? 'text-brand-700 font-semibold' : ''}
              ${step.status === 'completed' ? 'text-slate-700 font-medium' : ''}
              ${step.status === 'pending' ? 'text-slate-400' : ''}
            `}
          >
            {step.name}
          </span>

          {step.completedAt && step.status === 'completed' && (
            <span className="text-xs text-slate-400" title={formatDate(step.completedAt)}>
              {relativeTime(step.completedAt)}
            </span>
          )}

          {hasDetails && (
            <button
              onClick={() => setShowDetails(!showDetails)}
              className={`
                text-xs px-2 py-0.5 rounded-full border transition-colors cursor-pointer
                ${
                  showDetails
                    ? isCore
                      ? 'bg-amber-50 border-amber-300 text-amber-700'
                      : 'bg-brand-50 border-brand-300 text-brand-700'
                    : 'bg-slate-50 border-slate-200 text-slate-500 hover:border-slate-300'
                }
              `}
            >
              {showDetails ? 'hide' : 'details'}
            </button>
          )}
        </div>

        {showDetails && step.details && (
          <div className="mt-1.5">
            <TrackingDetails details={step.details} />
          </div>
        )}
      </div>
    </div>
  );
}
